import React, { useEffect, useState } from 'react';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// What OCR stored for a loyalty receipt (somo-circle1-api#777): the printed
// store/commissary, the matched MilitaryBase, totals, the location-mismatch
// hint and the printed line items. Shared by the review queue
// (LoyaltyReceipts.js, in ReceiptDetailsModal) and EditLoyaltyReceipt.js.
//
// The line items come from GET /admin/loyalty/receipts/:id/line-items, which
// is CircleOne-only, so callers only render this for non-MML admins.

const formatDollars = (amount) => (Number.isFinite(amount) ? `$${Number(amount).toFixed(2)}` : '—');
const orDash = (value) => (value === null || value === undefined || value === '' ? '—' : value);

// commissaryBaseId is populated by the admin GETs, but a PATCH response
// (EditLoyaltyReceipt's save) can hand back the bare id — show "matched"
// without a name rather than nothing.
const commissaryLabel = (base) => {
    if (!base) return null;
    if (typeof base === 'string') return 'Matched base';
    return [base.name, [base.city, base.state].filter(Boolean).join(', ')].filter(Boolean).join(' — ');
};

// The PATCH responses (edit, re-check OCR, restore version) return
// commissaryBaseId as a bare id, unlike the admin GETs. EditLoyaltyReceipt
// runs each save result through this to keep the base name from the GET when
// the save left the same base matched, so the panel doesn't drop to
// "Matched base" until a reload.
export const withPopulatedBase = (prev, next) => {
    const prevBase = prev?.commissaryBaseId;
    if (prevBase && typeof prevBase === 'object' && next?.commissaryBaseId === prevBase._id) {
        return { ...next, commissaryBaseId: prevBase };
    }
    return next;
};

const vendorAddressLabel = (r) => {
    const cityLine = [r.vendorCity, [r.vendorState, r.vendorZip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const parts = [r.vendorStreet, cityLine].filter(Boolean);
    return parts.length ? parts.join(', ') : r.vendorAddress || null;
};

// The state locationMismatch was computed from: the matched base's state,
// else the printed address's (commissaryState on the API). vendorState alone
// can be blank or differ when the base was matched by ZIP or name.
export const shopStateOf = (receipt) => receipt.commissaryState || receipt.vendorState || '?';

// locationMismatch: true/false when both the commissary's state and the
// member's profile state are known, null otherwise. Informational only — it
// never affects approval or points.
export function LocationBadge({ receipt }) {
    if (receipt.locationMismatch === true) {
        return (
            <span
                className="badge badge-warning fast-tooltip"
                data-tooltip="Informational only — members can shop at any commissary. This doesn't affect approval or points."
                title="Informational only — members can shop at any commissary. This doesn't affect approval or points."
            >
                Shopped in {shopStateOf(receipt)}, profile says {receipt.profileState || '?'}
            </span>
        );
    }
    if (receipt.locationMismatch === false) {
        return <span className="badge badge-success">Matches profile state ({receipt.profileState})</span>;
    }
    return <span className="badge badge-light">Unknown</span>;
}

function LineItemsTable({
    receiptId, s3Key, lineItemCount, updatedAt,
}) {
    const [state, setState] = useState({ status: 'loading', lineItems: [], count: lineItemCount ?? null });

    useEffect(() => {
        let cancelled = false;
        setState((prev) => ({ ...prev, status: 'loading' }));
        (async () => {
            try {
                const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/receipts/${receiptId}/line-items`);
                // A gateway error page isn't JSON — fall back to the generic
                // message rather than showing a parse error.
                const data = await res.json().catch(() => null);
                if (!res.ok || !data) throw new Error(data?.message || 'Could not load line items.');
                if (!cancelled) {
                    setState({
                        status: 'ready',
                        lineItems: data.data?.lineItems || [],
                        count: data.data?.lineItemCount ?? null,
                    });
                }
            } catch (err) {
                if (!cancelled) setState((prev) => ({ ...prev, status: 'error', error: err.message }));
            }
        })();
        return () => { cancelled = true; };
        // Refetch when the receipt's file (and so its active lines) changes,
        // or after any save — a "Re-check OCR" keeps the same file and may
        // keep the same count while rewriting the lines.
    }, [receiptId, s3Key, lineItemCount, updatedAt]);

    if (state.status === 'loading') {
        return (
            <div className="text-center py-3">
                <span className="spinner-border spinner-border-sm text-primary mr-2" role="status" aria-hidden="true" />
                Loading line items…
            </div>
        );
    }
    if (state.status === 'error') {
        return <div className="alert alert-danger mb-0">{state.error || 'Could not load line items.'}</div>;
    }
    if (state.lineItems.length === 0) {
        return (
            <p className="text-muted mb-0">
                {state.count === 0
                    ? 'OCR read this receipt but found no line items.'
                    : 'Line items aren\'t available for this receipt (OCR didn\'t read it, or it predates line-item capture).'}
            </p>
        );
    }

    return (
        <div className="table-responsive">
            <table className="table table-sm table-striped table-bordered mb-0" data-testid="receipt-line-items">
                <thead>
                    <tr>
                        <th style={{ width: '40px' }}>#</th>
                        <th>Description</th>
                        <th className="text-right" style={{ width: '60px' }}>Qty</th>
                        <th className="text-right" style={{ width: '90px' }}>Unit price</th>
                        <th className="text-right" style={{ width: '90px' }}>Total</th>
                        <th style={{ width: '130px' }}>SKU / UPC</th>
                    </tr>
                </thead>
                <tbody>
                    {state.lineItems.map((item) => (
                        <tr key={item._id || item.lineIndex}>
                            <td>{item.lineIndex + 1}</td>
                            <td title={item.rawText || undefined}>
                                {orDash(item.description)}
                                {item.wicItem && <span className="badge badge-info ml-2" title="Bought with WIC benefits">WIC</span>}
                            </td>
                            <td className="text-right">{orDash(item.quantity)}</td>
                            <td className="text-right">{formatDollars(item.unitPrice)}</td>
                            <td className="text-right">{formatDollars(item.lineTotal)}</td>
                            <td>{orDash(item.productCode)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// purchasedAt is the receipt's printed wall-clock time stored as if it were
// UTC (textract.js parseReceiptDateTime), so it's formatted in UTC to show
// exactly what was printed, not shifted into the admin's own timezone.
const formatPurchasedAt = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(undefined, { timeZone: 'UTC' });
};

export function ReceiptDetails({ receipt }) {
    const purchasedAt = formatPurchasedAt(receipt.purchasedAt);
    const rows = [
        ['Store', receipt.vendorName],
        ['Address', vendorAddressLabel(receipt)],
        ['Phone', receipt.vendorPhone],
        ['Store / register', [receipt.storeNumber && `Store ${receipt.storeNumber}`, receipt.registerNumber && `Register ${receipt.registerNumber}`]
            .filter(Boolean).join(' · ') || null],
        ['Matched commissary', commissaryLabel(receipt.commissaryBaseId) || 'No base match'],
        ['Receipt #', receipt.printedReceiptId],
        ['Purchased', purchasedAt || [receipt.receiptDate, receipt.receiptTime].filter(Boolean).join(' ') || null],
    ];

    return (
        <div data-testid="receipt-details">
            <div className="row">
                <div className="col-md-7">
                    <table className="table table-sm table-borderless mb-2">
                        <tbody>
                            {rows.map(([label, value]) => (
                                <tr key={label}>
                                    <th style={{ width: '150px' }}>{label}</th>
                                    <td>{orDash(value)}</td>
                                </tr>
                            ))}
                            <tr>
                                <th>Location</th>
                                <td><LocationBadge receipt={receipt} /></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="col-md-5">
                    <table className="table table-sm table-borderless mb-2">
                        <tbody>
                            <tr><th style={{ width: '110px' }}>Subtotal</th><td>{formatDollars(receipt.subtotal)}</td></tr>
                            <tr><th>Tax</th><td>{formatDollars(receipt.tax)}</td></tr>
                            <tr><th>Total</th><td>{formatDollars(receipt.grossTotal)}</td></tr>
                            {Number.isFinite(receipt.snapTenderedTotal) && (
                                <tr><th>SNAP</th><td>{formatDollars(receipt.snapTenderedTotal)}</td></tr>
                            )}
                            {/* somo-circle1-api#799. hasWic with no amount: the WIC
                                slip or a "WIC …" item, but no readable total. */}
                            {(Number.isFinite(receipt.wicTenderedTotal) || receipt.hasWic) && (
                                <tr>
                                    <th>WIC</th>
                                    <td>{Number.isFinite(receipt.wicTenderedTotal) ? formatDollars(receipt.wicTenderedTotal) : 'Yes, amount not read'}</td>
                                </tr>
                            )}
                            {receipt.paymentMethodLine && (
                                <tr><th>Payment</th><td>{receipt.paymentMethodLine}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <h6 className="mt-2">Line items</h6>
            <LineItemsTable
                receiptId={receipt._id}
                s3Key={receipt.s3Key}
                lineItemCount={receipt.lineItemCount}
                updatedAt={receipt.updatedAt}
            />
        </div>
    );
}

// Same overlay convention as the bulk-approve modal in LoyaltyReceipts.js;
// below the receipt preview (1060) so a receipt can still be opened full-size.
export function ReceiptDetailsModal({ receipt, onClose }) {
    const isOpen = Boolean(receipt);
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    if (!receipt) return null;
    const member = [receipt.userId?.name, receipt.userId?.lastName].filter(Boolean).join(' ');
    return (
        <div
            className="modal d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="receipt-details-title"
            style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1055 }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content" data-testid="receipt-details-modal">
                    <div className="modal-header">
                        <h5 className="modal-title" id="receipt-details-title">
                            Receipt details{member ? ` — ${member}` : ''}
                        </h5>
                        <button type="button" className="close" aria-label="Close" onClick={onClose}>
                            <span>&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <ReceiptDetails receipt={receipt} />
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
